import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { Typography } from "@circleco/compass/components/Typography";
import { useState, useCallback } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { t } from "@/i18n-js/instance";
import { mapParticipantsPerStatus } from "@/react/components/Chat/MessagingV2/RailBar/helpers";
import { BlockDmsModal } from "@/react/components/Chat/sharedV2/BlockDmsModal";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { ChatRoomParticipant } from "@/react/types/Chat/ChatRoomParticipant";
import type { Space } from "@/react/types/Space";

import { ConfirmationModal } from "./ConfirmationModal";
import { useBlockMember } from "./hooks/useBlockMember";
import { useLeaveSpace } from "./hooks/useLeaveSpace";
import { ParticipantListComponent } from "./ParticipantList/ParticipantListComponent";
import { ParticipantListTitle } from "./ParticipantListTitle";

interface ContentProps {
  className?: string;
  fetchNextPage: () => void;
  hasMoreSpaceMembers?: boolean;
  refetchSpaceMembers: () => void;
  space: Space;
  spaceMembers: any[];
}

export const Content = ({
  spaceMembers,
  space,
  refetchSpaceMembers,
  hasMoreSpaceMembers = false,
  fetchNextPage,
  className = "",
}: ContentProps) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const { isViewOnlyMasquerading } = usePunditUserContext();

  const onSuccessBlockMember = () => {
    refetchSpaceMembers();
  };

  const {
    blockMember,
    isBlocking,
    memberToBlock,
    toggleBlockDmsModal,
    handleUnblockMember,
    isBlockDmsModalOpen,
    setIsBlockDmsModalOpen,
  } = useBlockMember({
    onSuccess: onSuccessBlockMember,
  });

  const { leaveSpaceMutation, isLeaving, memberToDelete, setMemberToDelete } =
    useLeaveSpace({
      refetchSpaceMembers,
      spaceId: space.id,
      setIsConfirmationModalOpen,
    });

  const handleRemoveMember = useCallback(
    (member: ChatRoomParticipant) => {
      setMemberToDelete(member);
      setIsConfirmationModalOpen(true);
    },
    [setMemberToDelete],
  );

  const { onlineParticipants, offlineParticipants } =
    mapParticipantsPerStatus(spaceMembers);

  const handleBlockMember = useCallback(() => {
    if (memberToBlock) {
      blockMember({ memberId: memberToBlock.community_member_id });
    }
  }, [memberToBlock, blockMember]);

  return (
    <>
      <div
        id="scrollableDiv"
        className="p-0 md:pb-4"
        style={{ height: "100%", overflow: "auto" }}
      >
        <InfiniteScroll
          scrollThreshold={0.8}
          next={fetchNextPage}
          hasMore={hasMoreSpaceMembers}
          dataLength={spaceMembers.length}
          className={classNames(
            "p-0 sm:px-5 pb-12 bg-primary dark:bg-secondary",
            className,
          )}
          scrollableTarget="scrollableDiv"
          loader={
            <div className="flex justify-center">
              <Typography variant="label-xs-uppercase">
                {t("toggle_with_loader.loading")}
              </Typography>
            </div>
          }
        >
          {onlineParticipants.length > 0 && (
            <>
              <ParticipantListTitle title={t("chat_space.online")} />
              <ParticipantListComponent
                participantList={onlineParticipants}
                isViewOnlyMasquerading={isViewOnlyMasquerading}
                onBlockMemberDm={toggleBlockDmsModal}
                onRemoveMember={handleRemoveMember}
                onUnblockMemberDm={handleUnblockMember}
              />
            </>
          )}
          {offlineParticipants.length > 0 && (
            <>
              <ParticipantListTitle title={t("chat_space.offline")} />
              <ParticipantListComponent
                participantList={offlineParticipants}
                isViewOnlyMasquerading={isViewOnlyMasquerading}
                onBlockMemberDm={toggleBlockDmsModal}
                onRemoveMember={handleRemoveMember}
                onUnblockMemberDm={handleUnblockMember}
              />
            </>
          )}
        </InfiniteScroll>
      </div>
      <ConfirmationModal
        open={isConfirmationModalOpen}
        onOpenChange={setIsConfirmationModalOpen}
        onConfirm={() => leaveSpaceMutation()}
        isLoading={isLeaving}
        memberName={memberToDelete?.name}
        spaceName={space.name}
      />
      <BlockDmsModal
        showModal={isBlockDmsModalOpen}
        onClose={() => setIsBlockDmsModalOpen(false)}
        onConfirm={handleBlockMember}
        memberName={memberToBlock?.name || ""}
        isLoading={isBlocking}
      />
    </>
  );
};
